import { Feather } from '@expo/vector-icons';
import { useRoute, useNavigation } from '@react-navigation/native';
import { Button, Title, SpaceTypeHeader, WebWrapper } from '_/space-management/components';
import { DATE_FORMAT, DAY_MONTH_YEART_FORMAT } from '_/space-management/config/date';
import { colors } from '_/space-management/constants/theme';
import { formatDate } from '_/space-management/helpers/formatDate';
import { useDateContext } from '_/space-management/hooks/DateContext';
import { useSlotContext } from '_/space-management/hooks/SlotContext';
import { useSpaceContext } from '_/space-management/hooks/SpaceContext';
import { AppRoute } from '_/space-management/navigation/types';
import {
  getSpaceTypeColor,
  SpaceType,
} from '_/space-management/services/models/enums/space-type.enum';
import {
  DisabledSpaceDateType,
  SpacesModel,
} from '_/space-management/services/models/spaces.model';
import { parseISO } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { Calendar, DateData, LocaleConfig } from 'react-native-calendars';
import { showMessage } from 'react-native-flash-message';
import { Direction } from 'react-native-calendars/src/types';
import HandleTimePicker from './handleTimePicker';
import styles, {
  calendarTheme,
  selectedCustomMark,
  todayCustomMark,
  customDisabledDates,
} from './styles';

interface ParamsProps {
  locationName?: string;
  space: SpacesModel;
  availableSlotsCount: string;
}

export default function DatePicker() {
  const navigation = useNavigation<any>();
  const {
    setSelectedDay,
    selectedDay,
    today,
    isIntervalInvalid,
    hasStartTimeBeforeCurrent,
    minDate,
    maxDate,
    timePickerVisible,
    checkOpenCloseHourMin,
  } = useDateContext();
  const { setSlotSearchValue } = useSlotContext();
  const route = useRoute();
  const { availableSlotsCount, locationName, space } = route.params as ParamsProps;
  const [markedDates, setMarkedDates] = useState({});
  const { disabledDateData, currentSpace } = useSpaceContext();
  const invalidDate = (isIntervalInvalid || hasStartTimeBeforeCurrent) && timePickerVisible;
  const disabledButton = invalidDate || !selectedDay;
  const { t } = useTranslation();

  useEffect(() => {
    if (disabledDateData.length > 0) {
      const disabledDates = disabledDateData.map((item: DisabledSpaceDateType) =>
        parseISO(item.date)
      );
      const disabled = disabledDates.map((date) => {
        const aux = {
          [formatDate(today, DATE_FORMAT)]: { selected: true, customStyles: todayCustomMark },
          [selectedDay]: { selected: true, customStyles: selectedCustomMark(currentSpace.type) },
          [formatDate(date, DATE_FORMAT)]: {
            selected: true,
            customStyles: customDisabledDates,
          },
        };
        return aux;
      });
      const objDates = Object.values(Object.assign({}, disabled));
      const marks = objDates.reduce((a, b) => {
        return { ...a, ...b };
      }, {});
      setMarkedDates(marks);
    } else {
      const marks = {
        [formatDate(today, DATE_FORMAT)]: { selected: true, customStyles: todayCustomMark },
        [selectedDay]: { selected: true, customStyles: selectedCustomMark(currentSpace.type) },
      };
      setMarkedDates(marks);
    }
  }, [disabledDateData, selectedDay, space, today]);

  const goToSlots = useCallback(() => {
    if (
      !currentSpace.scheduleEnabled &&
      ((currentSpace.type === SpaceType.WORK && checkOpenCloseHourMin()) ||
        (timePickerVisible && checkOpenCloseHourMin()))
    ) {
      showMessage({
        message: t('spaceManage.error'),
        description: t('spaceManage.workstationScreen.errorMessages.checkOpenCloseHourMinError', {
          openAt: currentSpace?.openAtHourMin ?? '00:00',
          closeAt: currentSpace?.closeAtHourMin ?? '23:59',
        }),
        backgroundColor: colors.errorRed,
      });
      return;
    }

    if (currentSpace.type === SpaceType.WORK) {
      setSlotSearchValue(undefined);
    }
    navigation.navigate(AppRoute.WORK, {
      spaceType: currentSpace.type,
      locationName,
      availableSlotsCount,
    });
  }, [
    t,
    availableSlotsCount,
    locationName,
    navigation,
    setSlotSearchValue,
    currentSpace.type,
    currentSpace.scheduleEnabled,
    checkOpenCloseHourMin,
    timePickerVisible,
    currentSpace?.openAtHourMin,
    currentSpace?.closeAtHourMin,
  ]);

  LocaleConfig.locales['pt-BR'] = {
    monthNames: [
      t('spaceManage.months.january'),
      t('spaceManage.months.february'),
      t('spaceManage.months.march'),
      t('spaceManage.months.april'),
      t('spaceManage.months.may'),
      t('spaceManage.months.june'),
      t('spaceManage.months.july'),
      t('spaceManage.months.august'),
      t('spaceManage.months.september'),
      t('spaceManage.months.october'),
      t('spaceManage.months.november'),
      t('spaceManage.months.december'),
    ],
    monthNamesShort: [
      t('spaceManage.months.jan'),
      t('spaceManage.months.feb'),
      t('spaceManage.months.mar'),
      t('spaceManage.months.apr'),
      t('spaceManage.months.mayShort'),
      t('spaceManage.months.jun'),
      t('spaceManage.months.jul'),
      t('spaceManage.months.aug'),
      t('spaceManage.months.sep'),
      t('spaceManage.months.oct'),
      t('spaceManage.months.nov'),
      t('spaceManage.months.dec'),
    ],
    dayNames: [
      t('spaceManage.weekDays.sunday'),
      t('spaceManage.weekDays.monday'),
      t('spaceManage.weekDays.tuesday'),
      t('spaceManage.weekDays.wednesday'),
      t('spaceManage.weekDays.thursday'),
      t('spaceManage.weekDays.friday'),
      t('spaceManage.weekDays.saturday'),
    ],
    dayNamesShort: [
      t('spaceManage.weekDays.SU'),
      t('spaceManage.weekDays.M'),
      t('spaceManage.weekDays.T'),
      t('spaceManage.weekDays.W'),
      t('spaceManage.weekDays.TH'),
      t('spaceManage.weekDays.F'),
      t('spaceManage.weekDays.S'),
    ],
  };
  LocaleConfig.defaultLocale = 'pt-BR';

  const handleDayPress = useCallback(
    (day: DateData) => {
      const isDisabled = disabledDateData.filter(
        (item: DisabledSpaceDateType) => item.date.slice(0, 10) === day.dateString
      );

      if (isDisabled.length === 0) {
        setSelectedDay(day.dateString);
      } else {
        showMessage({
          message: t('spaceManage.closedSpace.closedSpaceMessage')
            .concat(' ')
            .concat(formatDate(parseISO(isDisabled[0].date), DAY_MONTH_YEART_FORMAT)),
          ...(isDisabled[0].reason && {
            description: t('spaceManage.closedSpace.reason').concat(': ').concat(isDisabled[0].reason),
          }),
          backgroundColor: colors.errorRed,
          duration: 5000,
        });
      }
    },
    [disabledDateData, setSelectedDay, t]
  );

  return (
    //<WebWrapper>
    <View style={styles.modal}>
      <SpaceTypeHeader
        spaceType={currentSpace.type}
        availableSlotsCount={availableSlotsCount}
        locationName={locationName}
        backButtonIsVisible={false}
      />
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollViewContainer}
      >
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'position' : undefined}
        >
          <Title title={t('spaceManage.datePickerScreen.whenDoYouWantToReserve')} />
          <Calendar
            theme={calendarTheme(currentSpace.type)}
            minDate={minDate?.toISOString()}
            maxDate={maxDate?.toISOString()}
            onDayPress={handleDayPress}
            markingType="custom"
            markedDates={markedDates}
            renderArrow={(direction: Direction) => (
              <Feather name={`chevron-${direction}` as any} size={28} color={colors.darkGrey} />
            )}
          />
          <HandleTimePicker space={currentSpace} />
          <View style={styles.wrapper}>
            <View style={{ opacity: disabledButton ? 0.2 : 1 }}>
              <Button
                backgroundColor={getSpaceTypeColor(currentSpace.type)}
                textColor={colors.white}
                disabled={disabledButton}
                onPress={goToSlots}
              >
                {t('spaceManage.next')}
              </Button>
            </View>
            <Button onPress={navigation.goBack}>{t('spaceManage.cancel')}</Button>
          </View>
        </KeyboardAvoidingView>
      </ScrollView>
    </View>
    //</WebWrapper>
  );
}
